import React, { type JSX, useState } from 'react';
import { Link, router, usePage } from '@inertiajs/react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import {
  Drawer,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  useTheme,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import Box from '@mui/material/Box';
import * as Sentry from '@sentry/react';
import ReadMore from '@mui/icons-material/ReadMore';

import Logo from '@/assets/logo.svg?react';
import LogoNoText from '@/assets/logo-no-text.svg?react';
import { Navigation } from '@/pages/auth/components/Navigation';
import { UserMenu } from '@/core/ui/UserMenu';
import { useBoolean } from '@/core/hooks/useBoolean';
import { FlashMessage, TFlashMessage } from '@/core/ui/FlashMessage';
import { Activity, Firm, User } from '@/types';
import GlobalEngagementsStatus from '@/layouts/GlobalEngagementsStatus';
import { useMixpanelPageTrack } from '@/core/hooks/useMixpanelPageTrack';
import { CollapsedNavigation } from '@/pages/auth/components/CollapsedNavigation';
import { useLocalStorage } from '@/core/hooks/useLocalStorage';
import { ActivityMenu } from '@/pages/activity/ActivityMenu';
import { FlashMessageProvider } from '@/core/hooks/FlashMessageContext';
import { SnackbarProvider } from '@/core/hooks/SnackbarContext';
import { FirmMenu } from '@/core/ui/FirmMenu';
import { can, firmRoute } from '@/core/utils';
import { Permission } from '@/core/const';

type Props = {
  children?: React.ReactElement;
};

type PageProps = {
  auth: { user: User };
  flash: TFlashMessage;
  activities?: Activity[];
  areAllActivitiesLoaded: boolean;
  hasUnviewedActivities: number;
  firms: Firm[];
  ziggy: {
    defaults: {
      firm: string;
    };
  };
};

export default function Authenticated({ children }: Props): JSX.Element {
  const theme = useTheme();
  const [isFullDrawerOpen, setFullDrawerOpen] = useLocalStorage(
    'isFullDrawerOpen',
    false,
  );
  const [moreActionsAnchor, setMoreActionsAnchor] =
    useState<HTMLElement | null>(null);

  const {
    props: {
      auth: { user },
      flash,
      activities,
      areAllActivitiesLoaded,
      hasUnviewedActivities,
      firms,
      ziggy,
    },
  } = usePage<PageProps>();
  const [_, setFirmPid] = useLocalStorage('firm_pid', {
    firmPid: ziggy.defaults.firm,
  });

  const [
    isHamburgerDrawerOpen,
    toggleIsHamburgerDrawerOpen,
    setIsHamburgerDrawerOpen,
  ] = useBoolean();

  const handleFirmSelect = (newFirm: Firm): void => {
    const path = window.location.pathname;
    const match = path.match(/^\/firms\/[0-9a-fA-F-]{36}(\/.*)$/);
    const afterPid = match ? match[1] : null;
    setFirmPid({ firmPid: newFirm.pid });
    router.visit(`/firms/${newFirm.pid}${afterPid ?? ''}`, {
      preserveScroll: true,
      preserveState: true,
    });
  };

  const handleAddNewEngagementClick = (): void => {
    router.visit(firmRoute('engagements.create'));
    setMoreActionsAnchor(null);
  };

  const handleAddNewTaxpayerClick = (): void => {
    router.visit(firmRoute('taxpayers.create'));
    setMoreActionsAnchor(null);
  };

  const handleAddNewProposalClick = (): void => {
    router.visit(firmRoute('proposals.create'));
    setMoreActionsAnchor(null);
  };

  useMixpanelPageTrack(user);

  Sentry.setUser({
    id: user.pid,
    email: user.email,
  });

  const drawerWidth = isHamburgerDrawerOpen || isFullDrawerOpen ? 256 : 80;

  const isTopLevelFirmRoute = ((): boolean => {
    const path = window.location.pathname;
    // Matches /firms/{pid}, /firms/{pid}/, /firms/{pid}/something, or /firms/{pid}/v2/engagements
    return (
      /^\/firms\/[0-9a-fA-F-]{36}(\/(?!.*\/)[^\/]*)?\/?$/.test(path) ||
      /^\/firms\/[0-9a-fA-F-]{36}\/v2\/engagements\/?$/.test(path)
    );
  })();

  const isFirmRoute = window.location.pathname.startsWith('/firms/');
  const canSeeFirmMenu =
    (can(user, Permission.MANAGE_TEAMS) ||
      can(user, Permission.TEAM_ALLOW_MULTIPLE_FIRMS)) &&
    firms.length > 1 &&
    isFirmRoute;

  return (
    <FlashMessageProvider>
      <Stack
        sx={{
          flexDirection: 'row',
        }}
      >
        <Stack sx={{ display: { xs: 'none', md: 'flex' } }}>
          <Drawer
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                boxSizing: 'border-box',
              },
            }}
            variant="permanent"
            anchor="left"
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                pt: 3,
                px: 2,
                pb: 2,
              }}
            >
              <Link href={route('home')}>
                {isFullDrawerOpen ? (
                  <Logo width={80} />
                ) : (
                  <LogoNoText width={48} />
                )}
              </Link>
            </Box>
            {isFullDrawerOpen ? (
              <Navigation
                handleItemClick={(): void => setIsHamburgerDrawerOpen(false)}
              />
            ) : (
              <CollapsedNavigation />
            )}
            <Stack
              sx={{
                alignItems: isFullDrawerOpen ? 'stretch' : 'center',
                gap: 2,
                p: isFullDrawerOpen ? 2 : 3,
                pt: 0,
              }}
            >
              <Box
                onClick={(event: React.MouseEvent<HTMLElement>): void =>
                  setMoreActionsAnchor(event.currentTarget)
                }
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.5,
                  py: 0.875,
                  borderRadius: 1,
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 400,
                  color: 'text.primary',
                  '& svg': {
                    width: 24,
                    height: 24,
                    color: 'action.active',
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  ...(!isFullDrawerOpen && {
                    justifyContent: 'center',
                    px: 0,
                  }),
                }}
              >
                <AddCircleIcon />
                {isFullDrawerOpen && 'Create'}
              </Box>
              <Menu
                open={!!moreActionsAnchor}
                anchorEl={moreActionsAnchor}
                onClose={() => setMoreActionsAnchor(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
              >
                <MenuItem onClick={handleAddNewEngagementClick}>
                  Create engagement
                </MenuItem>
                <MenuItem onClick={handleAddNewTaxpayerClick}>
                  Create client
                </MenuItem>
                <MenuItem onClick={handleAddNewProposalClick}>
                  Create proposal
                </MenuItem>
              </Menu>
              <Tooltip
                title={isFullDrawerOpen ? 'Collapse' : 'Expand'}
                placement="right"
              >
                <IconButton
                  onClick={(): void => setFullDrawerOpen(!isFullDrawerOpen)}
                  sx={{ alignSelf: 'center' }}
                >
                  {isFullDrawerOpen ? (
                    <ReadMore sx={{ transform: 'rotate(180deg)' }} />
                  ) : (
                    <ReadMore />
                  )}
                </IconButton>
              </Tooltip>
            </Stack>
          </Drawer>
        </Stack>
        <Box
          sx={{
            width: '100%',
          }}
        >
          <AppBar position="static">
            <Toolbar
              sx={{
                backgroundColor: theme.palette.common.white,
                px: 4,
                py: 1,
                display: 'flex',
                flexDirection: 'row',
                width: '100%',
              }}
            >
              <Stack sx={{ display: { md: 'none' } }}>
                <IconButton
                  size="large"
                  edge="start"
                  onClick={toggleIsHamburgerDrawerOpen}
                >
                  <MenuIcon />
                </IconButton>
              </Stack>
              {canSeeFirmMenu && (
                <FirmMenu
                  firms={firms}
                  selectedFirmPid={route().params.firm}
                  onSelect={handleFirmSelect}
                  disabled={!isTopLevelFirmRoute}
                />
              )}
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  gap: 2,
                  marginLeft: 'auto',
                }}
              >
                <ActivityMenu
                  activities={activities ?? []}
                  areAllActivitiesLoaded={areAllActivitiesLoaded ?? false}
                  activitiesViewedAt={
                    user.activitiesViewedAt
                      ? new Date(user.activitiesViewedAt)
                      : null
                  }
                  hasUnviewedActivities={hasUnviewedActivities}
                />
                <UserMenu
                  username={user.name}
                  profilePhoto={user.profile_photo_url}
                />
              </Stack>
            </Toolbar>
          </AppBar>
          <SnackbarProvider yPos={isFullDrawerOpen ? 288 : 110}>
            {children}
          </SnackbarProvider>
          <FlashMessage
            flash={flash}
            position={isFullDrawerOpen ? '288px' : '110px'}
          />
          <GlobalEngagementsStatus />
          {isHamburgerDrawerOpen && (
            <Drawer
              open={isHamburgerDrawerOpen}
              onClose={toggleIsHamburgerDrawerOpen}
              sx={{
                width: drawerWidth,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                  width: drawerWidth,
                  boxSizing: 'border-box',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: 4,
                }}
              >
                <Link href={route('home')}>
                  <Logo width={80} />
                </Link>
              </Box>
              <Navigation
                handleItemClick={(): void => setIsHamburgerDrawerOpen(false)}
              />
            </Drawer>
          )}
        </Box>
      </Stack>
    </FlashMessageProvider>
  );
}
